/**
 * Enum defining the location categories for STags.
 * These are used to group related elements in the application.
 *
 * Format: <general region>.<specific section>
 */
export enum Sel {
  InitialBilling = 'studio.initial-billing',
  // Agent-link create-studio wizard: the organization Select (trigger + per-org
  // items) and the resolved selected-org id. Mirrored in
  // studio/electron/e2e/helpers/selectors.ts.
  AgentLinkCreateStudio = 'agent-link.create-studio',
  // Agent-link claim screen (routes/agent/link/auth.tsx): its submit button.
  // Mirrored in studio/electron/e2e/helpers/selectors.ts.
  AgentLinkClaim = 'agent-link.claim',
  // Control Room - Left Tray
  MainRoomLeftTrayHeader = 'control-room.left-tray.header',
  MainRoomLeftTrayAudio = 'control-room.left-tray',
  MainRoomLeftTrayVideo = 'control-room.left-tray',

  // Control Room - Right Tray
  MainRoomRightTrayHeader = 'control-room.right-tray.header',
  MainRoomRightTrayControls = 'control-room.right-tray.controls',
  MainRoomRightTrayImageSettings = 'control-room.right-tray.image-settings',
  MainRoomRightTrayDeviceSetting = 'control-room.right-tray.device-setting',

  // Generic Section primitive (collapsible trigger). Shared across trays.
  // Per playwright-stag-selectors-only (ai.knowledge/platform): test
  // selectors target the section trigger by slug uniqueID, so the
  // namespace stays neutral instead of being tied to a particular tray.
  MainRoomSection = 'control-room.section',

  // Control Room - Bottom Tray
  MainRoomBottomTrayHeader = 'control-room.bottom-tray.header',
  MainRoomBottomTrayPresets = 'control-room.bottom-tray',
  MainRoomBottomTrayAutoTracking = 'control-room.bottom-tray.auto-tracking',

  // Local / cloud mode switch (media routing). Dual state stays live regardless.
  LocalMode = 'control-room.local-mode',

  // Control Room - Main Canvas
  MainRoomMainCanvasToolbar = 'control-room.main-canvas.toolbar',
  MainRoomMainCanvasSingle = 'control-room.main-canvas.single',
  MainRoomMainCanvasMulti = 'control-room.main-canvas.multi',

  // Per-source-tile configuration badge (Authenticate / Select model /
  // Connect controls / Calibrate camera). The tag is the badge status and the
  // uniqueID is the device id, so a test can assert a specific badge per tile.
  MainRoomConfigBadge = 'control-room.config-badge',
  MainRoomVideoBadge = 'control-room.video-badge',

  // Device Management
  AddVideoDeviceDiscovery = 'add-video-device.discovery',
  ConfigVideoDeviceVideo = 'config-video-device.video',
  ConfigVideoDeviceBrandModel = 'config-video-device.brand-model',
  ConfigVideoDeviceControl = 'config-video-device.control',

  // Bridge Management
  BridgeManagement = 'bridge-management',
  BridgeConnection = 'bridge-connection',

  // Settings
  Settings = 'settings',
  SettingsSwitcher = 'settings.switcher',
  AdvancedSettings = 'settings.advanced',
  VideoSettings = 'settings.video',
  AudioSettings = 'settings.audio',
  CredentialsEditor = 'editor.credentials',

  // Production
  Production = 'production',
  ProductionOutput = 'production.output',
  ProductionRecording = 'production.recording',
  ProductionSourceCard = 'production.source-card',

  // Room monitoring popover (header trigger lives on `control-room.header`,
  // set directly in routes/room/page.tsx). Covers the popover panel itself and
  // the per-device upstream Codec readout inside it, which the SSR-M5 HIL
  // preflight reads to prove the leased camera is still H.265.
  Monitoring = 'monitoring',

  // Account
  AccountDropdown = 'account.dropdown',
  AccountSettings = 'account.settings',

  BootOverlay = 'boot.overlay',
  Error = 'error',

  // Signup / onboarding
  ElectronLogin = 'auth.electron-login',
  // Post-auth prequalification wizard (ENG-4175). Controls live in
  // routes/register/prequal-questions.tsx + routes/agent/setup/post-auth-prequal.tsx.
  Prequalification = 'agent-setup.prequal',

  // Exhibits
  Exhibits = 'exhibits',
  NotificationStackExhibit = 'exhibits.notification-stack',

  // Notifications
  Notifications = 'notifications',

  // Shared trial lifecycle dialogs use the exact Studio as their identity.
  StandardTrialNotice = 'standard-trial.notice',
  TrialWelcome = 'trial-welcome',

  // Mid-session desktop update toast posted by the bridge watchdog
  // (agent/provider/watchdog.tsx). The toast body carries the offered version
  // as its uniqueID so a test can pin an assertion to one version, and every
  // action inside it is tagged so specs never select on product copy.
  // Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
  DesktopUpdateNag = 'notifications.update-nag',

  // Modals and Dialogs
  Modals = 'modals',
  Dialogs = 'dialogs',

  // Studio Picker (ENG-4037) — shared popped picker: workspace entry,
  // auto-link 2+ selection, and the header "add studio" modal.
  StudioPicker = 'studio-picker',

  // First-run funnel (ENG-3796). One entry per screen the FUNNEL draws. The
  // camera steps are the production Add Video Source modal now, so they are
  // tagged under that workflow's own subjects (AddVideoDeviceDiscovery,
  // ConfigVideoDeviceVideo, ConfigVideoDeviceBrandModel) rather than here —
  // there is no onboarding-only copy of those screens to tag.
  Permissions = 'permissions',
  OnboardingWelcome = 'onboarding.welcome',
  OnboardingBridgeConcept = 'onboarding.bridge-concept',
  OnboardingDetecting = 'onboarding.detecting',
  OnboardingFailure = 'onboarding.failure',
  OnboardingChooseMethod = 'onboarding.choose-method',
  OnboardingConnectionTest = 'onboarding.connection-test',
  OnboardingMagicMoment = 'onboarding.magic-moment',
  OnboardingComplete = 'onboarding.complete'
}

/**
 * STag type - determines whether the element is interactive or data-bearing
 */
export enum STagType {
  /** Interactive elements that can be clicked, typed into, etc. */
  Interactive = 'interactive',

  /** Elements that contain data to be read by tests */
  Data = 'data'
}

/**
 * Creates a data-prog-key attribute for an element (original implementation)
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns An object with the data-prog-key attribute
 */
export const STag = (
  location: Sel,
  tag: string,
  uniqueID?: string
): { ['data-prog-key']: string } => {
  const baseTag = [location, tag].join('.');
  const uniqueTag = uniqueID ? `${baseTag}:${uniqueID}` : baseTag;
  return { 'data-prog-key': uniqueTag.toLowerCase() };
};

/**
 * Creates a data-stag attribute for an element
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @param type - Optional tag type (interactive or data)
 * @returns An object with the data-stag attribute
 */
export const STagNew = (
  location: Sel,
  tag: string,
  uniqueID?: string,
  type: STagType = STagType.Interactive
): { ['data-stag']: string } => {
  const baseTag = [location, tag].join('.');
  const uniqueTag = uniqueID ? `${baseTag}:${uniqueID}` : baseTag;
  const tagWithType = `${type}:${uniqueTag.toLowerCase()}`;

  return { 'data-stag': tagWithType };
};

/**
 * Original interface for extracted STag data
 */
export interface STagExtraction {
  key: string;
}

/**
 * Extended interface for extracted STag data with type
 */
export interface STagExtractionNew extends STagExtraction {
  type: STagType;
}

/**
 * Extracts STag data from props (original implementation)
 *
 * @param props - Component props that may contain a data-prog-key
 * @returns Tuple with extracted STag data and remaining props
 */
export const STagExt = (props: any): [STagExtraction | undefined, any] => {
  const { 'data-prog-key': stag, ...rest } = props;
  return stag ? [{ key: stag }, rest] : [undefined, rest];
};

/**
 * Injects STag data into props (original implementation)
 *
 * @param stag - STag extraction data
 * @returns Object with data-prog-key attribute
 */
export const STagInj = (stag: STagExtraction | undefined): { ['data-prog-key']?: string } => {
  if (!stag) {
    return {};
  }
  return { 'data-prog-key': stag.key };
};

/**
 * Extracts STagNew data from props
 *
 * @param props - Component props that may contain a data-stag
 * @returns Tuple with extracted STagNew data and remaining props
 */
export const STagExtNew = (props: any): [STagExtractionNew | undefined, any] => {
  const { 'data-stag': stag, ...rest } = props;

  if (stag) {
    const [type, key] = stag.split(':', 2);
    return [
      {
        key,
        type: type as STagType
      },
      rest
    ];
  }

  return [undefined, rest];
};

/**
 * Injects STagNew data into props
 *
 * @param stag - STagNew extraction data
 * @returns Object with data-stag attribute
 */
export const STagInjNew = (stag: STagExtractionNew | undefined): { ['data-stag']?: string } => {
  if (!stag) {
    return {};
  }

  return { 'data-stag': `${stag.type}:${stag.key}` };
};

/**
 * Creates a data-stag attribute for a data element
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns An object with the data-stag attribute for a data element
 */
export const DataSTag = (
  location: Sel,
  tag: string,
  uniqueID?: string
): { ['data-stag']: string } => {
  return STagNew(location, tag, uniqueID, STagType.Data);
};
